"use client";

import React, { useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import CommonStringParser from "@/src/shared/components/CommonStringParser";
import { useLockBodyScroll } from "@/src/shared/hooks/useLockBodyScroll";

interface ISeeMoreModalProps {
  content: string;
  title?: string;
  maxLines?: number;
  className?: string;
  buttonText?: string;
  modalTitle?: string;
  showTitle?: boolean;
  buttonClassName?: string;
  contentClassName?: string;
}

const AboutDetailsModal: React.FC<ISeeMoreModalProps> = ({
  content,
  title = "More about this home",
  maxLines = 6,
  className = "",
  buttonText = "Show more",
  modalTitle,
  showTitle = true,
  buttonClassName = "",
  contentClassName = "",
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [canToggle, setCanToggle] = React.useState(false);
  const [clampPx, setClampPx] = React.useState<number | null>(null);

  const previewRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const processedContent = (content || "").split("\n").join("<br/>");
  const finalModalTitle = modalTitle || title;

  const reduced = useReducedMotion();

  // detect overflow for "see more"
  const recompute = React.useCallback(() => {
    if (!previewRef.current || !measureRef.current) return;
    const style = window.getComputedStyle(previewRef.current);
    const lh = parseFloat(style.lineHeight || "0") || 24;
    const clampHeight = Math.round(lh * maxLines);
    setClampPx(clampHeight);
    const natural = measureRef.current.scrollHeight;
    setCanToggle(natural > clampHeight + 1);
  }, [maxLines]);

  useEffect(() => {
    recompute();
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(recompute)
        : null;
    if (previewRef.current) ro?.observe(previewRef.current);
    window.addEventListener("resize", recompute);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [recompute, processedContent]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeModal();
    if (isModalOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isModalOpen]);

  useLockBodyScroll(isModalOpen);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const previewStyle: React.CSSProperties =
    clampPx !== null ? { maxHeight: clampPx, overflow: "hidden" } : {};

  // ---------- Animations (Tailwind handles md breakpoints) ----------
  const EASE = [0.22, 1, 0.36, 1] as const;

  // Backdrop: opacity only (no blur → avoids scroll flicker)
  const BACKDROP = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: reduced ? { duration: 0.18 } : { duration: 0.35, ease: EASE },
    },
    exit: {
      opacity: 0,
      transition: reduced ? { duration: 0.18 } : { duration: 0.9, ease: EASE },
    },
  } as const;

  // Desktop panel: clip-path wipe (top→bottom) on exit, 0.9s
  const PANEL = {
    initial: reduced
      ? { opacity: 0 }
      : { opacity: 0, clipPath: "inset(100% 0% 0% 0%)" },
    animate: reduced
      ? { opacity: 1, transition: { duration: 0.2 } }
      : {
          opacity: 1,
          clipPath: "inset(0% 0% 0% 0%)",
          transition: { duration: 0.45, ease: EASE },
        },
    exit: reduced
      ? { opacity: 0, transition: { duration: 0.2 } }
      : {
          opacity: 0.999,
          clipPath: "inset(100% 0% 0% 0%)",
          transition: { duration: 0.9, ease: EASE },
        },
  } as const;

  // Mobile sheet: same wipe for consistency
  const SHEET = {
    initial: { clipPath: "inset(100% 0% 0% 0%)" },
    animate: {
      clipPath: "inset(0% 0% 0% 0%)",
      transition: reduced ? { duration: 0.2 } : { duration: 0.45, ease: EASE },
    },
    exit: {
      clipPath: "inset(100% 0% 0% 0%)",
      transition: reduced ? { duration: 0.2 } : { duration: 0.9, ease: EASE },
    },
  } as const;

  // drag-to-close only when scrolled to top (mobile)
  const [dragEnabled, setDragEnabled] = React.useState(true);
  React.useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const onScroll = () => setDragEnabled(el.scrollTop <= 0);
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [isModalOpen]);

  const onDragEnd = (
    _: unknown,
    info: { offset: { y: number }; velocity: { y: number } },
  ) => {
    const { y: travel } = info.offset;
    const { y: vel } = info.velocity;
    if (travel > 120 || vel > 800) closeModal();
  };

  return (
    <div className={className}>
      {showTitle && <p className="font-bold text-lg mb-2">{title}</p>}

      {/* Preview */}
      <div className={`font-normal text-base my-3 ${contentClassName}`}>
        <div
          ref={previewRef}
          className="transition-all duration-200"
          style={{
            ...previewStyle,
            display: "-webkit-box",
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: "vertical",
            lineHeight: "1.5em",
          }}
        >
          <CommonStringParser dangerHtml={processedContent || ""} />
        </div>

        {/* Hidden measurer */}
        <div
          ref={measureRef}
          className="invisible absolute left-0 top-0 -z-10 pointer-events-none w-full"
          style={{ contain: "layout" }}
        >
          <CommonStringParser dangerHtml={processedContent || ""} />
        </div>

        {/* See more button */}
        {canToggle && (
          <motion.button
            onClick={openModal}
            type="button"
            className={`mt-2 inline-flex items-center px-3 py-1 rounded-md text-xs font-medium  border-divider-100 bg-coral-pink-50 text-coral-pink-500 ${buttonClassName}`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {buttonText}
          </motion.button>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence initial={false} mode="wait">
        {isModalOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              className="fixed inset-0 z-[100] bg-black/40"
              variants={BACKDROP}
              initial="initial"
              animate="animate"
              exit="exit"
              onClick={closeModal}
              style={{ willChange: "opacity" }}
            />

            {/* Desktop panel (md and up) */}
            <motion.div
              key="panel-desktop"
              role="dialog"
              aria-modal="true"
              className="hidden md:flex fixed inset-0 z-[101] items-center justify-center p-10 pointer-events-none"
              variants={PANEL}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ willChange: "clip-path, opacity" }}
            >
              <div className="pointer-events-auto w-full max-w-2xl max-h-[80vh] py-4 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                {/* ChatHeading */}
                <div className="flex items-center justify-between px-6 py-1 border-b border-divider-100 bg-white sticky top-0 z-10">
                  <h2 className="text-xl font-bold text-ink-black">
                    {finalModalTitle}
                  </h2>
                  <motion.button
                    onClick={closeModal}
                    className="p-2 hover:bg-neutral-50 rounded-full transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Close"
                    type="button"
                  >
                    <X className="w-6 h-6 text-ink-gray" />
                  </motion.button>
                </div>

                {/* Body */}
                <div
                  className="flex-1 min-h-0 overflow-y-auto py-4"
                  style={{
                    contain: "paint",
                    transform: "translateZ(0)",
                    WebkitOverflowScrolling: "touch",
                    overscrollBehaviorY: "contain",
                  }}
                >
                  <div className="px-6 pb-6">
                    <div className="prose prose-neutral max-w-none">
                      <div className="text-sm leading-relaxed text-ink-black">
                        <CommonStringParser
                          dangerHtml={processedContent || ""}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Mobile bottom sheet (below md) */}
            <motion.div
              key="sheet-mobile"
              role="dialog"
              aria-modal="true"
              className="block md:hidden fixed inset-x-0 bottom-0 z-[101]"
              variants={SHEET}
              initial="initial"
              animate="animate"
              exit="exit"
              drag={dragEnabled ? "y" : false}
              dragDirectionLock
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.2 }}
              onDragEnd={onDragEnd}
              style={{ willChange: "clip-path" }}
            >
              <div className="mx-auto w-full max-w-xl rounded-t-2xl bg-white shadow-[0_-8px_32px_rgba(0,0,0,0.18)] overflow-hidden">
                {/* handle + header */}
                <div className="sticky top-0 bg-white z-10">
                  <div className="flex items-center justify-center py-2">
                    <span className="h-1.5 w-12 rounded-full bg-neutral-300" />
                  </div>
                  <div className="flex items-center justify-between px-4 pb-3 border-b border-divider-100">
                    <h2 className="text-base font-semibold text-ink-black">
                      {finalModalTitle}
                    </h2>
                    <motion.button
                      onClick={closeModal}
                      className="p-2 -mr-1 rounded-full hover:bg-neutral-50"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label="Close"
                      type="button"
                    >
                      <X className="w-5 h-5 text-ink-gray" />
                    </motion.button>
                  </div>
                </div>

                {/* scrollable content */}
                <div
                  ref={contentRef}
                  className="max-h-[70vh] overflow-y-auto px-4 pb-5 pt-4"
                  style={{
                    contain: "paint",
                    transform: "translateZ(0)",
                    WebkitOverflowScrolling: "touch",
                    overscrollBehaviorY: "contain",
                  }}
                >
                  <div className="prose prose-neutral max-w-none">
                    <div className="text-sm leading-relaxed text-ink-black">
                      <CommonStringParser dangerHtml={processedContent || ""} />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AboutDetailsModal;
